// NPM Imports
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Toastr } from "@revvsales/components";

// Components Imports
import { ChartHeader, ColumnChart } from "../Charts";
import DashboardLoader from "../DashboardLoader";

// Actions Imports
import DashboardActions from "../../../actions/DashboardActions";
import { filterObjFromArray } from "../../../actions/DashboardActions/dashboardMethods";

// Internal Imports
import { PIPELINE, DASHBOARD } from "../../../constants";
import { OppoUtils, GlobalUtils } from "../../../utils";

// Destructuring
const { getDashboardDocDeals } = DashboardActions;
const { DOCUMENT_STATUS_HASMAP } = PIPELINE;
const { DOCUMENT_TYPES_HASMAP } = DASHBOARD;
const { getDocStatusClassName } = OppoUtils;
const { numberForamting, handleApiErrMsg } = GlobalUtils;

class PipelineOverview extends Component {
  state = { showLoader: false };

  componentDidMount() {
    this._onInitData();
  }

  _toggleLoader = flag => {
    this.setState({ showLoader: flag });
  };

  _onInitData = () => {
    const { apiConfigs: { userID, fromDate, toDate } = {} } = this.props;
    this._toggleLoader(true);
    this.props
      .getDashboardDocDeals(userID, fromDate, toDate)
      .then(() => {
        this._toggleLoader(false);
      })
      .catch(error => {
        console.error(
          `DocumentStatusBoard(index.js) => _onInitData => ${error}`,
        );
        this._toggleLoader(false);
        Toastr.showToastr({
          type: "failure",
          message: handleApiErrMsg(error),
          timeout: 3000,
        });
      });
  };

  _getTotalDocData = pipelineDocs => {
    const docTotal = {
      count: 0,
      dealValue: 0,
    };
    pipelineDocs.forEach(docStatus => {
      docTotal.count += docStatus.frequency;
      docTotal.dealValue += docStatus.dealValue;
    });

    return docTotal;
  };

  _getTextNumberBox = (icon, title, value, count, isLable, labelClassName) => (
    <div
      className="documentstatusboard-itemsbox-wrapper first-itemsbox-wrapper itemsbox-totalcount"
      key={title}>
      {icon ? (
        <div className="documentstatusboard-itemsbox-icon documentstatusboard-itemsbox-documenticon">
          <FontAwesomeIcon icon={["fas", `${icon}`]} />
        </div>
      ) : null}

      <div className="documentstatusboard-itemsbox">
        {isLable ? (
          <span
            className={`documentstatusboard-${labelClassName} documentstatusboard-itemsbox-status`}>
            {title}
          </span>
        ) : (
          <span className="documentstatusboard-itemsbox-decription">
            {title}
          </span>
        )}

        <div
          className={`documentstatusboard-itemsbox-count ${
            !isLable ? "large-font" : ""
          }`}>
          {count}
        </div>
        <div className="documentstatusboard-itemsbox-decription">
          {numberForamting(value, "$")}
        </div>
      </div>
    </div>
  );

  _getDocStatusBoxes = pipelineDocs =>
    pipelineDocs.map(docStatus => {
      const labelClassName = getDocStatusClassName(docStatus.status);
      const title = DOCUMENT_STATUS_HASMAP[docStatus.status]
        ? DOCUMENT_STATUS_HASMAP[docStatus.status].toLowerCase()
        : null;

      return this._getTextNumberBox(
        null,
        title,
        docStatus.dealValue,
        docStatus.frequency,
        true,
        labelClassName,
      );
    });

  _getItemBoxes = () => {
    const { pipelineDocs, dealCounts } = this.props;
    const docTotal = this._getTotalDocData(pipelineDocs);
    const approvedDeals = filterObjFromArray(
      dealCounts,
      "name",
      "DOCUMENT_CLIENT_APPROVED",
    )[0];
    const achievedValue = approvedDeals ? approvedDeals.value : 0;
    const items = [
      {
        title: "Docs in Pipeline",
        value: docTotal.count,
        icon: "file-alt",
      },
      {
        title: "Net Value in Pipeline",
        value: numberForamting(docTotal.dealValue, "$"),
        icon: "dollar-sign",
      },
      {
        title: "Achieved Value",
        value: numberForamting(achievedValue, "$"),
        icon: "bullseye",
      },
    ];
    return (
      <div className="pipelineoverview-header-wrapper">
        {items.map(item => (
          <div className="pipelineoverview-itemsbox-wrapper" key={item.title}>
            <div className="pipelineoverview-itemsbox-icon document-icon">
              <FontAwesomeIcon icon={["fas", `${item.icon}`]} />
            </div>
            <div className="pipelineoverview-itemsbox">
              <span className="pipelineoverview-itemsbox-title">
                {item.title}
              </span>
              <div className="pipelineoverview-itemsbox-number">
                {item.value}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Chart Tool Text :: for showing the required data when hover on chart
  _getChartToolText = data => `
      <div class="chart-tooltext-wrapper min-width">
        <div class="chart-tooltext-title-wrapper">
          <div class="tooltext-title">
            ${DOCUMENT_STATUS_HASMAP[data.status]} - ${data.frequency} 
          </div>
          <span class="tooltext-value">
            Total Deal value: <b class="primary-color"> ${numberForamting(
              data.dealValue,
              "$",
            )}</b> 
          </span>
        </div>
        ${data.types
          .map(
            type =>
              `<div class="chart-tooltext-row">
                <span class="chart-tooltext-row-title row-small-title">
                  ${DOCUMENT_TYPES_HASMAP[type.name]}
                </span> - 
                <span class="chart-tooltext-row-value">
                  ${type.frequency}
                </span>
              </div>`,
          )
          .join("")}
      </div>
    `;

  _getChartData = data =>
    data.map(d => ({
      label: DOCUMENT_STATUS_HASMAP[d.status],
      value: d.frequency,
      tooltext: this._getChartToolText(d),
    }));

  _getChart = () => {
    const chartConfig = {
      chart: {
        caption: "Documents in Pipeline",
        baseFontSize: "11",
      },
      data: this._getChartData(this.props.pipelineDocs),
    };
    return (
      <div className="pipelineoverview-chart-wrapper">
        <ColumnChart width="100%" height={250} configs={chartConfig} />
      </div>
    );
  };

  /**
   * Decide the Pipeline Overview
   */
  _getView = () => {
    const { documentTyes } = this.props;
    return (
      <Fragment>
        {/* Header Section */}
        <ChartHeader
          title="Pipeline Overview"
          dateTitle="Jan 01, 2019 July 01, 2019"
        />
        {/* ItemBox Section */}
        {this._getItemBoxes(documentTyes)}

        {/* Chart Section */}
        {this._getChart()}
      </Fragment>
    );
  };

  render() {
    const { apiConfigs: { userRole: { name: userType } } = {} } = this.props;
    if (this.state.showLoader) {
      return <DashboardLoader message="loading document deals" />;
    }
    return <Fragment>{this._getView(userType)}</Fragment>;
  }
}

const mapStateToProps = state => {
  const { pipelineDocs, dealCounts, documentTyes } = state.DashboardStore;
  return { pipelineDocs, dealCounts, documentTyes };
};

const mapDispatchToProps = {
  getDashboardDocDeals: (userID, fromDate, toDate) =>
    getDashboardDocDeals(userID, fromDate, toDate),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PipelineOverview);



// WEBPACK FOOTER //
// ./src/components/DashboardWrapper/PipelineOverview/index.js